// src/pages/Chat/PollDetailsModal.jsx
import React from "react";
import { FiX, FiDownload } from "react-icons/fi";
import { resolvePhotoUrl } from "../../utils/photoHelper";
import useChatStore from "../../store/chatStore";
import useAuthStore from "../../store/authStore";
import * as XLSX from "xlsx";

const GRADIENTS = [
  "from-violet-600 to-blue-600",
  "from-blue-600 to-cyan-500",
  "from-emerald-600 to-teal-500",
  "from-rose-600 to-pink-500",
  "from-amber-500 to-orange-500",
  "from-indigo-600 to-purple-600",
];

function getStringHash(str = "") {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

function getGradient(str) {
  return GRADIENTS[getStringHash(str) % GRADIENTS.length];
}

function getInitials(name = "") {
  return (
    name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?"
  );
}

// ── Excel report builder ──────────────────────────────────────────────────────

function downloadPollReport(pollData, votes, members, getVoteTimeRaw) {
  const rows = [];

  (pollData.options || []).forEach((option, optIdx) => {
    const voters = Object.entries(votes)
      .filter(([, votedIdx]) => votedIdx === optIdx)
      .map(([userId]) => userId);

    if (voters.length === 0) {
      rows.push({
        "Poll Question": pollData.question,
        "Option": option,
        "Employee ID": "—",
        "Employee Name": "—",
        "Voted At": "—",
        "Total Votes for Option": 0,
      });
    } else {
      voters.forEach((userId, i) => {
        const member = members.find((m) => m.employee_id === userId);
        rows.push({
          "Poll Question": i === 0 ? pollData.question : "",
          "Option": i === 0 ? option : "",
          "Employee ID": userId,
          "Employee Name": member?.full_name || userId,
          "Voted At": getVoteTimeRaw(userId) || "Unknown",
          "Total Votes for Option": i === 0 ? voters.length : "",
        });
      });
    }
  });

  // Summary row
  const totalVotes = Object.values(votes).filter((v) => v !== null).length;
  rows.push({});
  rows.push({
    "Poll Question": "TOTAL VOTES",
    "Option": "",
    "Employee ID": "",
    "Employee Name": "",
    "Voted At": "",
    "Total Votes for Option": totalVotes,
  });

  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [
    { wch: 40 }, { wch: 22 }, { wch: 14 },
    { wch: 24 }, { wch: 22 }, { wch: 22 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Poll Report");

  const safeQ = (pollData.question || "poll").replace(/[^a-z0-9]/gi, "_").slice(0, 30);
  XLSX.writeFile(wb, `poll_report_${safeQ}.xlsx`);
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function PollDetailsModal({ pollData, votes, messages, pollSenderId, onClose }) {
  const conversations  = useChatStore((s) => s.conversations);
  const currentUserId  = useChatStore((s) => s.currentUserId);
  const currentUserRole = useAuthStore((s) => s.role); // "ADMIN" | "MANAGER" | "EMPLOYEE"

  const conv    = conversations.find((c) => c.id === pollData.conversation);
  const members = conv?.members || [];

  // ── Permission: sender OR admin member of this conversation ────────────────
  const isSender      = !!currentUserId && !!pollSenderId && currentUserId === pollSenderId;
  const isAdminInConv =
    currentUserRole === "ADMIN" &&
    members.some((m) => m.employee_id === currentUserId);
  const canDownload = isSender || isAdminInConv;

  // ── Vote time helpers ──────────────────────────────────────────────────────
  const getVoteMsg = (userId) =>
    messages.find(
      (m) =>
        m.msg_type === "POLL_VOTE" &&
        (m.sender_id === userId || m.sender?.employee_id === userId)
    );

  const getVoteInfo = (userId) => {
    const voteMsg = getVoteMsg(userId);
    if (!voteMsg) return null;
    const date  = new Date(voteMsg.created_at);
    const now   = new Date();
    const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const dateStr = date.toDateString() === now.toDateString()
      ? "Today"
      : date.toLocaleDateString([], { month: "short", day: "numeric" });
    return `${dateStr} at ${timeStr}`;
  };

  const getVoteTimeRaw = (userId) => {
    const voteMsg = getVoteMsg(userId);
    return voteMsg ? new Date(voteMsg.created_at).toLocaleString() : null;
  };

  const getMember = (userId) => members.find((m) => m.employee_id === userId);

  return (
    <>
      <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed z-[121] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm h-[85vh] max-h-[600px] flex flex-col">
        <div className="bg-[#111b21] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col h-full text-slate-200">

          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-[#202c33]">
            <button onClick={onClose} className="p-1 text-slate-400 hover:text-white transition-colors">
              <FiX size={20} />
            </button>
            <span className="text-sm font-semibold flex-1">Poll details</span>

            {/* Download button — only for poll sender or admin members */}
            {canDownload && (
              <button
                onClick={() => downloadPollReport(pollData, votes, members, getVoteTimeRaw)}
                title="Download poll responses as Excel"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-600/30 hover:text-emerald-300 text-[11px] font-semibold transition-all active:scale-95"
              >
                <FiDownload size={13} />
                Report
              </button>
            )}
          </div>

          {/* Content — unchanged voter list */}
          <div className="flex-1 overflow-y-auto p-5 space-y-8" style={{ scrollbarWidth: "thin" }}>
            <h2 className="text-lg font-bold text-slate-100">{pollData.question}</h2>

            {(pollData.options || []).map((option, optIdx) => {
              const voters = Object.entries(votes)
                .filter(([, votedIdx]) => votedIdx === optIdx)
                .map(([userId]) => userId);

              return (
                <div key={optIdx} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-300">{option}</span>
                    <span
                      className={`text-[11px] px-2.5 py-0.5 rounded-full font-bold ${
                        voters.length > 0
                          ? "bg-[#00a884]/20 text-[#00a884]"
                          : "bg-white/5 text-slate-500"
                      }`}
                    >
                      {voters.length} vote{voters.length !== 1 ? "s" : ""}{" "}
                      {voters.length > 0 && "★"}
                    </span>
                  </div>

                  <div className="space-y-5">
                    {voters.map((userId) => {
                      const member      = getMember(userId);
                      const voteTime    = getVoteInfo(userId);
                      const isYou       = userId === currentUserId;
                      const displayName = isYou ? "You" : member?.full_name || userId;
                      const memberPhoto = resolvePhotoUrl(member?.profile_photo);

                      return (
                        <div key={userId} className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center border border-white/5 shadow-md">
                            {memberPhoto ? (
                              <img src={memberPhoto} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div
                                className={`w-full h-full bg-gradient-to-br ${getGradient(userId)} flex items-center justify-center text-white text-sm font-semibold select-none`}
                              >
                                {getInitials(displayName)}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-slate-200">{displayName}</div>
                            <div className="text-xs text-slate-500">{voteTime || "Voted"}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </>
  );
}
